/**
 * Utility functions for formatting course IDs consistently
 * Base IDs: 01:198:211 (with colons)
 * Section IDs: 01:198:211:12345 (baseId:sectionId)
 */

/**
 * Normalizes a base ID to the format 01:198:211
 * Handles various input formats:
 * - "01:198:211" -> "01:198:211"
 * - "198:211" -> "01:198:211" (Rutgers subject/catalog shorthand)
 * - "01198211" -> "01:198:211"
 * - "198211" -> "01:198:211" (Rutgers subject/catalog shorthand)
 * - "01 198 211" -> "01:198:211"
 */
export function normalizeBaseId(baseId: string | null | undefined): string {
  if (!baseId) return '';
  
  // If already in correct format, return as-is
  if (/^\d{2}:\d{3}:\d{3}$/.test(baseId)) {
    return baseId;
  }

  // Assistant providers frequently omit Rutgers' institution prefix. This
  // product's catalog is Rutgers-only, so the six-digit subject/catalog form
  // is unambiguously the New Brunswick `01` school code.
  const shorthandMatch = baseId.trim().match(/^(\d{3})\s*[:\-\s]?\s*(\d{3})$/);
  if (shorthandMatch) {
    return `01:${shorthandMatch[1]}:${shorthandMatch[2]}`;
  }
  
  // Remove all non-digit characters
  const digits = baseId.replace(/\D/g, '');
  
  // If we have 8 digits, format as 01:198:211
  if (digits.length === 8) {
    return `${digits.slice(0, 2)}:${digits.slice(2, 5)}:${digits.slice(5, 8)}`;
  }

  if (digits.length === 6) {
    return `01:${digits.slice(0, 3)}:${digits.slice(3, 6)}`;
  }
  
  // If we have the format already, just clean it up
  const parts = baseId.split(':').map(p => p.trim()).filter(Boolean);
  if (parts.length === 3 && parts.every(p => /^\d+$/.test(p))) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(3, '0')}:${parts[2].padStart(3, '0')}`;
  }
  
  // Return original if we can't parse it
  return baseId;
}

/**
 * Strict identity normalization for Rutgers courses. Unlike normalizeBaseId,
 * this rejects malformed labels rather than returning them as hashable text.
 * Section identifiers are reduced to their eight-digit base course identity.
 */
export function normalizeRutgersBaseCourseId(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const sectionMatch = trimmed.match(/^(\d{2}):(\d{3}):(\d{3}):\d+$/);
  if (sectionMatch) return `${sectionMatch[1]}:${sectionMatch[2]}:${sectionMatch[3]}`;

  const compactSectionMatch = trimmed.match(/^(\d{8})\d+$/);
  if (compactSectionMatch) return normalizeBaseId(compactSectionMatch[1]);

  if (/^\d{2}[:\-]\d{3}[:\-]\d{3}$/.test(trimmed) || /^\d{8}$/.test(trimmed)) {
    return normalizeBaseId(trimmed);
  }
  if (/^\d{3}[:\-]\d{3}$/.test(trimmed) || /^\d{6}$/.test(trimmed)) {
    return normalizeBaseId(trimmed);
  }
  return null;
}

/**
 * Formats a section ID as baseId:sectionId (e.g., "01:198:211:12345")
 * @param baseId - The course base ID (will be normalized)
 * @param sectionId - The section ID (typically a 5-digit number or course.id)
 */
export function formatSectionId(baseId: string | null | undefined, sectionId: string | null | undefined): string {
  const normalizedBase = normalizeBaseId(baseId);
  if (!sectionId) return normalizedBase;
  
  // Extract the numeric part of sectionId if it contains other characters
  // sectionId might be just "12345" or "01:198:211:12345" or "course-id-12345"
  let sectionPart = sectionId;
  
  // If sectionId already contains the baseId format, extract just the section part
  if (sectionId.includes(':')) {
    const parts = sectionId.split(':');
    if (parts.length === 4) {
      // Already in format baseId:sectionId
      return sectionId;
    }
    // Take the last part as the section
    sectionPart = parts[parts.length - 1];
  }
  
  // Extract numeric section ID (typically 5 digits)
  const numericSection = sectionPart.replace(/\D/g, '');
  
  if (numericSection) {
    return `${normalizedBase}:${numericSection}`;
  }
  
  // Fallback: use the original sectionId
  return `${normalizedBase}:${sectionId}`;
}

/**
 * Extracts base ID from a section ID
 * "01:198:211:12345" -> "01:198:211"
 */
export function extractBaseIdFromSectionId(sectionId: string): string {
  if (!sectionId) return '';
  
  // If it's in format baseId:sectionId, extract baseId
  if (sectionId.includes(':')) {
    const parts = sectionId.split(':');
    if (parts.length >= 3) {
      // Take first 3 parts as baseId
      return normalizeBaseId(parts.slice(0, 3).join(':'));
    }
  }
  
  // Try to normalize as baseId
  return normalizeBaseId(sectionId);
}

/**
 * Formats course data for AI consumption
 * Ensures baseId and id fields are always in the correct format
 */
export function formatCourseForAI(course: {
  baseId?: string | null;
  id?: string | null;
  [key: string]: any;
}): {
  baseId: string;
  id: string; // Section ID in format baseId:sectionId
  [key: string]: any;
} {
  const normalizedBase = normalizeBaseId(course.baseId);
  const sectionId = formatSectionId(course.baseId, course.id);
  
  return {
    ...course,
    baseId: normalizedBase,
    id: sectionId,
  };
}
